import { computed, effect, inject } from '@angular/core';
import { Prettify, patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { SavedCartSearchResult } from '@idealsupply/ngclient-webservice-shopping-cart';
import { AuthenticationStore } from 'authentication-data';
import { SavedCartStore } from '../data-stores/saved-cart.store';
import { DialogService } from 'common-ui';
import { HttpErrorResponse } from '@angular/common/http';
import { EntityRecord } from 'state-data';

export const SavedCartsStore = signalStore(
  { providedIn: 'root' },
  withState(() => ({
    _savedCartRecord: undefined as EntityRecord<SavedCartSearchResult> | undefined,
  })),
  withComputed((store) => {
    const savedCartsLoading = computed(() => store._savedCartRecord()?.loading ?? false);
    const savedCartsError = computed(() => store._savedCartRecord()?.error);
    const savedCarts = computed(() => store._savedCartRecord()?.entity?.data ?? []);

    return {
      savedCartsLoading,
      savedCartsError,
      savedCarts,
    };
  }),
  withMethods(() => {
    const dialogService = inject(DialogService);

    const savedCartStore = inject(SavedCartStore);
    const saveCartAs = async (name: string) => {
      try {
        return await savedCartStore.saveCartAs(name);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to save cart: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    const replaceSavedCart = async (id: number) => {
      try {
        return await savedCartStore.replaceSavedCart(id);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to replace cart: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    const mergeToSavedCart = async (id: number) => {
      try {
        return await savedCartStore.mergeToSavedCart(id);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to merge cart: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    const deleteSavedCart = async (id: number) => {
      try {
        return await savedCartStore.deleteSavedCart(id);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to delete cart: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    return {
      saveCartAs,
      replaceSavedCart,
      mergeToSavedCart,
      deleteSavedCart,
    };
  }),
  withHooks((store) => {
    const savedCartStore = inject(SavedCartStore);
    const authStore = inject(AuthenticationStore);
    return {
      onInit() {
        effect(() => {
          const isAuthenticated = authStore.isAuthenticated();
          const record = isAuthenticated ? savedCartStore.getAll()() : undefined;
          patchState(store, { _savedCartRecord: record });
        });
      },
    };
  }),
);

export type SavedCartsStore = Prettify<InstanceType<typeof SavedCartsStore>>;
